/**
 * Auth-server attribution endpoints: countersign (compose path) and
 * verify-stamps (render path). Same fetch/token pattern as useThreadAcl.
 *
 * Both helpers are FAIL-OPEN by design:
 * - A message must NEVER be blocked or delayed on countersign failure — we
 *   cap the call at a hard time budget and fall back to stamp:null.
 * - A history render must never break on verify failure — callers just leave
 *   messages unverified (muted chip) when this returns null.
 */
const API_BASE = '/api/auth';
const TOKEN_KEY = 'foreman-auth-token';

/** Hard time budget for the countersign round-trip. The send proceeds with
 *  stamp:null the moment this expires — attribution is best-effort, delivery
 *  is not. */
export const COUNTERSIGN_TIMEOUT_MS = 600;

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * POST /api/auth/countersign — ask the auth server to stamp "this user is
 * sending into this thread right now". Returns the stamp, or null on any
 * failure/timeout. One stamp per message; nothing is cached.
 */
export async function requestCountersign(
  threadKey: string,
  timeoutMs: number = COUNTERSIGN_TIMEOUT_MS
): Promise<string | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_BASE}/countersign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ threadKey }),
      signal: ctrl.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as { stamp?: unknown } | null;
    return typeof data?.stamp === 'string' && data.stamp ? data.stamp : null;
  } catch {
    return null; // network error, abort, CORS — all mean "send unstamped"
  } finally {
    clearTimeout(timer);
  }
}

export interface StampVerifyResult {
  ok: boolean;
  username?: string;
}

/**
 * POST /api/auth/verify-stamps — batch-check countersigns pulled from a
 * shared thread's history. `results` is index-aligned with the input.
 * Returns null on any failure (callers render everything as unverified).
 */
export async function verifyStamps(stamps: string[]): Promise<StampVerifyResult[] | null> {
  if (stamps.length === 0) return [];
  try {
    const res = await fetch(`${API_BASE}/verify-stamps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ items: stamps.map((stamp) => ({ stamp })) }),
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as { results?: unknown } | null;
    if (!Array.isArray(data?.results)) return null;
    return (data!.results as Array<{ ok?: unknown; username?: unknown }>).map((r) => ({
      ok: r?.ok === true,
      username: typeof r?.username === 'string' ? r.username : undefined,
    }));
  } catch {
    return null;
  }
}
